import { audiencerateApi } from '../../audiencerate';
import { TableResponse } from '../types';
import {
  CampaignResponse,
  CampaignEventV2,
  CampaignProviderStatsV2,
  CampaignStatsV2,
  CampaignV2Response,
  CreateCampaignQueryArgs,
  CreateCampaignRequestBody,
  CreateCampaignV2RequestBody,
  DeleteCampaignArgs,
  GetCampaignArg,
  GetCampaignsArg,
  GetCampaignsCountArg,
  GetCampaignsCountV2Arg,
  GetCampaignEventsV2Arg,
  GetCampaignsV2Arg,
  GetEmailTemplateArg,
  GetEmailTemplatesArg,
  GetWhatsappTemplateArg,
  GetWhatsappTemplatesArg,
  UpsertEmailTemplateRequestBody,
  UpsertWhatsappTemplateRequestBody,
  UpdateCampaignArgs,
  UpdateCampaignRequestBody,
  UpdateCampaignV2RequestBody,
  EmailTemplateResponse,
  WhatsappTemplateResponse,
  WhatsappTemplateResponseItem,
} from './types';

const WHATSAPP_TEMPLATES_BASE_PATH = 'userjourney/whatsapp/templates';
const EMAIL_TEMPLATES_BASE_PATH = 'userjourney/mail/templates';

export const campaignsApi = audiencerateApi.injectEndpoints({
  endpoints: (build) => ({
    createCampaign: build.mutation<CampaignResponse, CreateCampaignRequestBody & CreateCampaignQueryArgs>({
      query: ({ workspaceId, ...body }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Campaigns'],
    }),
    getCampaigns: build.query<TableResponse<CampaignResponse>, GetCampaignsArg>({
      query: ({ workspaceId, limit, offset, searchText: search }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns`,
        params: { limit, offset, search },
      }),
      providesTags: ['Campaigns'],
    }),
    getCampaign: build.query<CampaignResponse, GetCampaignArg>({
      query: ({ workspaceId, campaignId }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns/${campaignId}`,
      }),
      providesTags: (result, error, args) => [{ type: 'Campaigns', id: args.campaignId }],
    }),
    getCampaignsCount: build.query<number, GetCampaignsCountArg>({
      query: ({ workspaceId, searchText: search, status }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns/count`,
        params: { search, status },
      }),
      providesTags: ['CampaignsCount'],
    }),
    deleteCampaign: build.mutation<void, DeleteCampaignArgs>({
      query: ({ workspaceId, campaignId }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns/${campaignId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Campaigns'],
    }),
    updateCampaign: build.mutation<void, UpdateCampaignArgs & UpdateCampaignRequestBody>({
      query: ({ workspaceId, campaignId, ...body }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns/${campaignId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Campaigns'],
    }),
    createCampaignV2: build.mutation<CampaignV2Response, CreateCampaignV2RequestBody & CreateCampaignQueryArgs>({
      query: ({ workspaceId, ...body }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Campaigns', 'CampaignsCount'],
    }),
    updateCampaignV2: build.mutation<CampaignV2Response, UpdateCampaignArgs & UpdateCampaignV2RequestBody>({
      query: ({ workspaceId, campaignId, ...body }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2/${campaignId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Campaigns', 'CampaignsCount'],
    }),
    getCampaignsV2: build.query<TableResponse<CampaignV2Response>, GetCampaignsV2Arg>({
      query: ({ workspaceId, limit, offset, searchText: search, status, providerType }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2`,
        params: { limit, offset, search, status, providerType },
      }),
      providesTags: ['Campaigns'],
    }),
    getCampaignV2: build.query<CampaignV2Response, GetCampaignArg>({
      query: ({ workspaceId, campaignId }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2/${campaignId}`,
      }),
      providesTags: (result, error, args) => [{ type: 'Campaigns', id: args.campaignId }],
    }),
    getCampaignsCountV2: build.query<number, GetCampaignsCountV2Arg>({
      query: ({ workspaceId, searchText: search, status, providerType }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2/count`,
        params: { search, status, providerType },
      }),
      providesTags: ['CampaignsCount'],
    }),
    deleteCampaignV2: build.mutation<void, DeleteCampaignArgs>({
      query: ({ workspaceId, campaignId }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2/${campaignId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Campaigns', 'CampaignsCount'],
    }),
    getCampaignStatsV2: build.query<CampaignStatsV2, GetCampaignArg>({
      query: ({ workspaceId, campaignId }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2/${campaignId}/stats`,
      }),
      providesTags: (result, error, args) => [{ type: 'Campaigns', id: args.campaignId }],
    }),
    getCampaignEventsV2: build.query<TableResponse<CampaignEventV2>, GetCampaignEventsV2Arg>({
      query: ({ workspaceId, campaignId, limit, offset }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2/${campaignId}/events`,
        params: { limit, offset },
      }),
      providesTags: (result, error, args) => [{ type: 'Campaigns', id: args.campaignId }],
    }),
    getCampaignStatsByProviderV2: build.query<CampaignProviderStatsV2[], CreateCampaignQueryArgs>({
      query: ({ workspaceId }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2/stats/by-provider`,
      }),
      providesTags: ['Campaigns'],
    }),
    getWhatsappTemplates: build.query<WhatsappTemplateResponse[], GetWhatsappTemplatesArg>({
      query: ({ workspaceId, limit, offset, searchText: search }) => ({
        url: `/v1/workspace/${workspaceId}/${WHATSAPP_TEMPLATES_BASE_PATH}`,
        params: { limit, offset, search },
      }),
      providesTags: ['Campaigns'],
    }),
    getWhatsappTemplate: build.query<WhatsappTemplateResponseItem, GetWhatsappTemplateArg>({
      query: ({ workspaceId, templateId }) => ({
        url: `/v1/workspaces/${workspaceId}/${WHATSAPP_TEMPLATES_BASE_PATH}/${templateId}`,
      }),
      providesTags: (result, error, args) => [{ type: 'Campaigns', id: args.templateId }],
    }),
    createWhatsappTemplate: build.mutation<WhatsappTemplateResponseItem, UpsertWhatsappTemplateRequestBody & CreateCampaignQueryArgs>({
      query: ({ workspaceId, ...body }) => ({
        url: `/v1/workspaces/${workspaceId}/${WHATSAPP_TEMPLATES_BASE_PATH}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Campaigns'],
    }),
    updateWhatsappTemplate: build.mutation<WhatsappTemplateResponseItem, UpsertWhatsappTemplateRequestBody & GetWhatsappTemplateArg>({
      query: ({ workspaceId, templateId, ...body }) => ({
        url: `/v1/workspaces/${workspaceId}/${WHATSAPP_TEMPLATES_BASE_PATH}/${templateId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Campaigns'],
    }),
    deleteWhatsappTemplate: build.mutation<void, GetWhatsappTemplateArg>({
      query: ({ workspaceId, templateId }) => ({
        url: `/v1/workspaces/${workspaceId}/${WHATSAPP_TEMPLATES_BASE_PATH}/${templateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Campaigns'],
    }),
    getEmailTemplates: build.query<EmailTemplateResponse[], GetEmailTemplatesArg>({
      query: ({ workspaceId, limit, offset, searchText: search }) => ({
        url: `/v1/workspace/${workspaceId}/${EMAIL_TEMPLATES_BASE_PATH}`,
        params: { limit, offset, search },
      }),
      providesTags: ['Campaigns'],
    }),
    getEmailTemplate: build.query<EmailTemplateResponse, GetEmailTemplateArg>({
      query: ({ workspaceId, templateId }) => ({
        url: `/v1/workspaces/${workspaceId}/${EMAIL_TEMPLATES_BASE_PATH}/${templateId}`,
      }),
      providesTags: (result, error, args) => [{ type: 'Campaigns', id: args.templateId }],
    }),
    createEmailTemplate: build.mutation<EmailTemplateResponse, UpsertEmailTemplateRequestBody & CreateCampaignQueryArgs>({
      query: ({ workspaceId, ...body }) => ({
        url: `/v1/workspaces/${workspaceId}/${EMAIL_TEMPLATES_BASE_PATH}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Campaigns'],
    }),
    updateEmailTemplate: build.mutation<EmailTemplateResponse, UpsertEmailTemplateRequestBody & GetEmailTemplateArg>({
      query: ({ workspaceId, templateId, ...body }) => ({
        url: `/v1/workspaces/${workspaceId}/${EMAIL_TEMPLATES_BASE_PATH}/${templateId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Campaigns'],
    }),
    deleteEmailTemplate: build.mutation<void, GetEmailTemplateArg>({
      query: ({ workspaceId, templateId }) => ({
        url: `/v1/workspaces/${workspaceId}/${EMAIL_TEMPLATES_BASE_PATH}/${templateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Campaigns'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateCampaignMutation,
  useGetCampaignsQuery,
  useGetCampaignsCountQuery,
  useLazyGetCampaignsQuery,
  useGetCampaignQuery,
  useDeleteCampaignMutation,
  useUpdateCampaignMutation,
  useCreateCampaignV2Mutation,
  useUpdateCampaignV2Mutation,
  useGetCampaignsV2Query,
  useLazyGetCampaignsV2Query,
  useGetCampaignV2Query,
  useGetCampaignsCountV2Query,
  useDeleteCampaignV2Mutation,
  useGetCampaignStatsV2Query,
  useGetCampaignEventsV2Query,
  useGetCampaignStatsByProviderV2Query,
  useGetWhatsappTemplatesQuery,
  useGetWhatsappTemplateQuery,
  useCreateWhatsappTemplateMutation,
  useUpdateWhatsappTemplateMutation,
  useDeleteWhatsappTemplateMutation,
  useGetEmailTemplatesQuery,
  useGetEmailTemplateQuery,
  useCreateEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
} = campaignsApi;
